import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

const SettingsModal = ({ isOpen, onClose, onSave }) => {
  const [startDate, setStartDate] = useState('');
  const [batchStartTime, setBatchStartTime] = useState('');
  const [batchEndTime, setBatchEndTime] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Get security pass from environment variable or use default for testing
  const getSecurityPass = () => {
    return process.env.REACT_APP_SECURITY_PASS || 'hexas123';
  };

  // Load existing settings when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedSettings = localStorage.getItem('course_settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setStartDate(settings.startDate || '');
        setBatchStartTime(settings.batchStartTime || '');
        setBatchEndTime(settings.batchEndTime || '');
      } else {
        setStartDate('');
        setBatchStartTime('');
        setBatchEndTime('');
      }
      setIsAuthenticated(false);
      setSecurityCode('');
      setError('');
    }
  }, [isOpen]);

  const handleSecuritySubmit = (e) => {
    e.preventDefault();
    
    if (!securityCode.trim()) {
      setError('Please enter the security code');
      return;
    }

    if (securityCode !== getSecurityPass()) {
      setError('Invalid security code. Please try again.');
      return;
    }

    setError('');
    setIsAuthenticated(true);
  };

  const handleSettingsSubmit = (e) => {
    e.preventDefault();

    if (!startDate || !batchStartTime || !batchEndTime) {
      setError('Please fill in all fields');
      return;
    }

    // Save to localStorage
    const settings = {
      startDate,
      batchStartTime,
      batchEndTime,
      lastUpdated: new Date().toISOString()
    };
    
    localStorage.setItem('course_settings', JSON.stringify(settings));
    setError('');
    onSave(settings);
    onClose();
  };

  const handleCancel = () => {
    setIsAuthenticated(false);
    setSecurityCode('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Course Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>
        
        {!isAuthenticated ? (
          // Security Code Form
          <form onSubmit={handleSecuritySubmit} className="space-y-4">
            <div className="text-center mb-6">
              <p className="text-gray-600">
                Please enter the security code to access course settings.
              </p>
            </div>
            
            <div>
              <label htmlFor="securityCode" className="block text-sm font-medium text-gray-700 mb-2">
                Security Code
              </label>
              <input
                type="password"
                id="securityCode"
                value={securityCode}
                onChange={(e) => setSecurityCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter security code"
                autoFocus
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Verify
              </button>
            </div>
          </form>
        ) : (
          // Settings Form
          <form onSubmit={handleSettingsSubmit} className="space-y-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                Course Start Date
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="batchStartTime" className="block text-sm font-medium text-gray-700 mb-2">
                Batch Start Time
              </label>
              <input
                type="time"
                id="batchStartTime"
                value={batchStartTime}
                onChange={(e) => setBatchStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="batchEndTime" className="block text-sm font-medium text-gray-700 mb-2">
                Batch End Time
              </label>
              <input
                type="time"
                id="batchEndTime"
                value={batchEndTime}
                onChange={(e) => setBatchEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SettingsModal; 